import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Dropdown, Tooltip, OverlayTrigger} from 'react-bootstrap'
import Select from 'react-select'
import {Formik, Form} from 'formik'
import {KTSVG} from '../../../../_metronic/helpers/components/KTSVG'

export type LinguistNoteFilterValues = {
  startDate: string
  endDate: string
  createdByIds: string[]
  tagIds: number[]
  accountIds: number[]
}

export type TagOption = {
  value: number
  label: string
}

export type CreatorOption = {
  value: string
  label: string
}

export type AccountOption = {
  value: number
  label: string
}

type Props = {
  initialValues: LinguistNoteFilterValues
  tagOptions: TagOption[]
  creatorOptions: CreatorOption[]
  accountOptions: AccountOption[]
  onApply: (values: LinguistNoteFilterValues) => void
  onReset: () => void
}

const isAnyFilterSelected = (values: LinguistNoteFilterValues) =>
  Boolean(
    values.startDate ||
      values.endDate ||
      (values.createdByIds?.length ?? 0) > 0 ||
      (values.tagIds?.length ?? 0) > 0 ||
      (values.accountIds?.length ?? 0) > 0
  )

const LinguistNotesFilterDropdown: React.FC<Props> = ({
  initialValues,
  tagOptions,
  creatorOptions,
  accountOptions,
  onApply,
  onReset,
}) => {
  const [showDropdownMenu, setShowDropdownMenu] = useState(false)
  const [isFilterApplied, setIsFilterApplied] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsFilterApplied(isAnyFilterSelected(initialValues))
  }, [initialValues])

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node
    const isInsideDropdown = dropdownRef.current?.contains(target)
    const isInsidePortal = (target as Element)?.closest?.('.react-select__menu')
    if (!isInsideDropdown && !isInsidePortal) {
      setShowDropdownMenu(false)
    }
  }, [])

  useEffect(() => {
    if (showDropdownMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdownMenu, handleClickOutside])

  const dropdownId = useMemo(
    () => `linguist-notes-filter-${Math.random().toString(36).slice(2)}`,
    []
  )

  return (
    <Dropdown ref={dropdownRef} show={showDropdownMenu} onToggle={(next) => setShowDropdownMenu(next)} autoClose={false}>
      <OverlayTrigger placement='top' overlay={<Tooltip id='tooltip-filter'>Filter</Tooltip>}>
        <Dropdown.Toggle
          variant='primary'
          id={dropdownId}
          className={`btn btn-icon btn-sm no-caret ${isFilterApplied ? 'btn-light-danger' : ''}`}
        >
          <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-primary' />
        </Dropdown.Toggle>
      </OverlayTrigger>

      {showDropdownMenu && (
        <Dropdown.Menu className='p-4 w-350px' align='end' show>
          <div className='px-4 pb-4'>
            <div className='fs-4 text-dark fw-semibolder'>Filter</div>
          </div>

          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={(values, {setSubmitting}) => {
              setSubmitting(true)
              onApply(values)
              setSubmitting(false)
              setShowDropdownMenu(false)
            }}
          >
            {({values, setFieldValue, handleSubmit, resetForm, dirty, isSubmitting}) => (
              <Form
                onSubmit={(e: any) => {
                  e.preventDefault()
                  handleSubmit()
                }}
              >
                <div className='separator border-gray-200'></div>
                <div className='card px-4 mt-4'>
                  <div className='row flex-column'>
                    <div className='col-12 mb-3'>
                      <label className='form-label fs-7 mb-1'>User</label>
                      <Select
                        isMulti
                        classNamePrefix='react-select'
                        options={creatorOptions}
                        value={creatorOptions.filter(
                          (o) => Array.isArray(values.createdByIds) && values.createdByIds.includes(o.value)
                        )}
                        onChange={(opts: any) =>
                          setFieldValue('createdByIds', opts ? opts.map((o: any) => o.value) : [])
                        }
                        placeholder='Select user(s)'
                        menuPortalTarget={document.body}
                        styles={{menuPortal: (base: any) => ({...base, zIndex: 9999})}}
                      />
                    </div>

                    <div className='col-12 mb-3'>
                      <label className='form-label fs-7 mb-1'>Tags</label>
                      <Select
                        isMulti
                        classNamePrefix='react-select'
                        options={tagOptions}
                        value={tagOptions.filter(
                          (o) => Array.isArray(values.tagIds) && values.tagIds.includes(o.value)
                        )}
                        onChange={(opts: any) =>
                          setFieldValue('tagIds', opts ? opts.map((o: any) => o.value) : [])
                        }
                        placeholder='Select tag(s)'
                        menuPortalTarget={document.body}
                        styles={{menuPortal: (base: any) => ({...base, zIndex: 9999})}}
                      />
                    </div>

                    <div className='col-12 mb-3'>
                      <label className='form-label fs-7 mb-1'>Account</label>
                      <Select
                        isMulti
                        classNamePrefix='react-select'
                        options={accountOptions}
                        value={accountOptions.filter(
                          (o) => Array.isArray(values.accountIds) && values.accountIds.includes(o.value)
                        )}
                        onChange={(opts: any) =>
                          setFieldValue('accountIds', opts ? opts.map((o: any) => o.value) : [])
                        }
                        placeholder='Select account(s)'
                        menuPortalTarget={document.body}
                        styles={{menuPortal: (base: any) => ({...base, zIndex: 9999})}}
                      />
                    </div>

                    <div className='col-12 mb-3'>
                      <label className='form-label fs-7 mb-1'>Start Date</label>
                      <input
                        type='date'
                        className='form-control form-control-sm'
                        value={values.startDate || ''}
                        onChange={(e) => setFieldValue('startDate', e.target.value)}
                      />
                    </div>

                    <div className='col-12 mb-3'>
                      <label className='form-label fs-7 mb-1'>End Date</label>
                      <input
                        type='date'
                        className='form-control form-control-sm'
                        value={values.endDate || ''}
                        onChange={(e) => setFieldValue('endDate', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className='text-end pt-4 px-4'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light me-2'
                    onClick={() => {
                      resetForm()
                      onReset()
                      setShowDropdownMenu(false)
                    }}
                  >
                    Reset
                  </button>
                  <button type='submit' className='btn btn-sm btn-primary' disabled={isSubmitting || !dirty}>
                    Apply
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </Dropdown.Menu>
      )}
    </Dropdown>
  )
}

export default LinguistNotesFilterDropdown
